import * as sentry from '@sentry/nextjs';
import { StoryData } from 'storyblok-js-client';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer-ts';

import { csrClient } from '../apollo/api/avantArteClient';
import { AvantArteArtistsSearchGateway } from '../gateways/AvantArteArtistsSearchGateway';
import { AugmentedArtistCard } from '../types/Artists';
import { Franchises } from '../types/Franchises';
import { GlossaryIndex, GlossaryItem, GlossaryTerm } from '../types/Glossary';
import { augmentArtists, mapArtistPageContentToArtist } from './artists';
import { InsightItem, mapFranchiseToItem } from './journals/insights';
import {
  getStoryblokPage,
  GetStoryblokPageOptions,
  getStoryblokPaginated,
  StoriesResponse,
  StoryResponse,
} from './storyblok';
import { removePunctuation } from './strings';

const artistsSearchGateway = new AvantArteArtistsSearchGateway(csrClient);

export type GlossaryLink = {
  full_slug: string;
  name: string;
};

type GlossaryRef = GlossaryLink & {
  index: number;
};

export const fetchGlossary = async (options: GetStoryblokPageOptions = {}) => {
  const glossary = await getStoryblokPaginated<GlossaryTerm>({
    is_startpage: false,
    starts_with: 'glossary/',
    ...options,
  });
  return glossary;
};

export const fetchGlossaryIndex = async (
  options: GetStoryblokPageOptions = {},
) => {
  const index: StoryResponse<GlossaryIndex> = await getStoryblokPage(
    'cdn/stories/glossary',
    {
      resolve_links: 'story',
      ...options,
    },
  );
  return index?.data.story || null;
};

export const fetchGlossaryResults = async (
  term: string,
  options: GetStoryblokPageOptions = {},
) => {
  const results: StoriesResponse<StoryData> = await getStoryblokPage(
    'cdn/stories',
    {
      by_slugs: 'artists/*,events/*,insights/*,products/*',
      excluding_slugs: 'glossary/*',
      filter_query: {
        __or: [
          { article: { like: `*${term}*` } },
          { details: { like: `*${term}*` } },
          { description: { like: `*${term}*` } },
          { practiceOverview: { like: `*${term}*` } },
        ],
      },
      page: 1,
      per_page: 50,
      ...options,
    },
  );
  return results?.data.stories || [];
};

export const fetchByGlossaryTerm = async (
  uuid: string,
  options: GetStoryblokPageOptions = {},
) => {
  let artistsData: AugmentedArtistCard[] = [];
  let insightsData: InsightItem[] = [];

  const stories = await getStoryblokPaginated<ArtistPageContent | Franchises>({
    by_slugs: 'artists/*,insights/*',
    filter_query: {
      glossary: {
        any_in_array: uuid,
      },
    },
    ...options,
  });

  const artists = stories.filter(
    ({ content }) => content.component === 'artist',
  ) as ArtistPageContent[];

  if (artists.length > 0) {
    const artistsCards = artists.map(mapArtistPageContentToArtist);
    const slugs = artistsCards.map(({ slug }) => slug);
    const internalArtists = await artistsSearchGateway.getArtistsBySlug(slugs);
    artistsData = await augmentArtists(artistsCards, internalArtists);
  }

  const insights = stories.filter(
    ({ content }) => content.component === 'franchise',
  ) as Franchises[];

  if (insights.length > 0) {
    insightsData = insights.map(mapFranchiseToItem);
  }

  return { artistsData, insightsData };
};

export const mapGlossaryLinks = (glossary: GlossaryTerm[]): GlossaryLink[] => {
  return glossary
    .map((term) => ({
      full_slug: term.full_slug,
      name: term.content.term,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const mapGlossaryTerms = (glossary: GlossaryTerm[]): GlossaryItem[] => {
  return glossary
    .map((term) => ({
      description: term.content.description,
      href: `/${term.full_slug}`,
      slug: term.slug,
      title: term.content.term,
      uuid: term.uuid,
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
};

const matchTermInText = (term: string, value: string) => {
  const word = term.toLowerCase();
  const text = value.toLowerCase();

  const wholeWordMatch =
    removePunctuation(text).split(' ').indexOf(word) !== -1;
  const multiWordMatch = word.split(' ').length > 1 && text.includes(word);
  if (wholeWordMatch || multiWordMatch) {
    const index = text.indexOf(word);
    return { index, text: value.substring(index, index + term.length) };
  }
  return undefined;
};

const matchTermInMatches = (term: string, matches: Record<string, boolean>) => {
  const word = term.toLowerCase();
  const keys = Object.keys(matches).join(' ').toLowerCase();
  return keys.includes(word);
};

const nodeIsLink = (node: StoryblokRichtext['content'][0]) => {
  return node.marks?.[0]?.type === 'link';
};

export const linkGlossaryTermsForRichText = (
  glossary: GlossaryTerm[],
  richText: StoryblokRichtext,
  matchesMap: Record<string, boolean> = {},
) => {
  const sortedGlossary = glossary.sort(
    (a, b) => b.content.term.length - a.content.term.length,
  );

  const parseContent = (content: StoryblokRichtext['content'] = []) => {
    return content.reduce((acc, curr) => {
      let text = curr.text || '';
      if (nodeIsLink(curr) || !text.trim()) {
        return [...acc, curr];
      }

      const refs: GlossaryRef[] = [];
      sortedGlossary.forEach((term) => {
        const found = matchTermInText(term.content.term, text);
        const taken = matchTermInMatches(term.content.term, matchesMap);
        if (found && !taken) {
          refs.push({
            full_slug: `/${term.full_slug}`,
            index: found.index,
            name: found.text,
          });
          matchesMap[term.content.term] = true;
        }
      });

      if (refs.length === 0) return [...acc, curr];

      const anchors = refs
        .sort((a, b) => a.index - b.index)
        .reduce((nodes, ref, index) => {
          const [start, ...rest] = text.split(ref.name);
          text = rest.join(ref.name);
          const anchor = {
            attrs: {
              href: ref.full_slug,
              linktype: 'glossary',
            },
            type: 'link',
          };
          return [
            ...nodes,
            { text: start || null, type: 'text' },
            { marks: [anchor], text: ref.name, type: 'text' },
            {
              text: index === refs.length - 1 ? text : null,
              type: 'text',
            },
          ];
        }, []);

      return [...acc, ...anchors] as StoryblokRichtext['content'];
    }, []);
  };

  return {
    ...richText,
    content: (richText.content || []).reduce((richTextContent, node) => {
      switch (node.type) {
        case 'bullet_list':
          return [
            ...richTextContent,
            {
              ...node,
              content: node.content?.map((listItem) => ({
                ...listItem,
                content: listItem.content?.map((paragraph) => ({
                  ...paragraph,
                  content: parseContent(paragraph.content),
                })),
              })),
            },
          ];
        case 'paragraph':
          return [
            ...richTextContent,
            {
              ...node,
              content: parseContent(node.content),
            },
          ];
        default:
          return [...richTextContent, node];
      }
    }, []),
  };
};

export const richTextWithGlossary = (
  content: StoryblokRichtext = { type: 'doc', content: [] },
  glossary: GlossaryTerm[] = [],
) => {
  try {
    const parsed = linkGlossaryTermsForRichText(glossary, content, {});
    return parsed;
  } catch (err) {
    sentry.captureException(err);
  }
  return content;
};
